import { createSignal, createEffect, For, Show } from 'solid-js';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import BotLicenseAssignmentList from './BotLicenseAssignmentList';
import type { BotLicenseAssignmentDto } from '../api';
import type { GrantTypeDto, UserGrantSummaryDto } from '../services/grantsApi';
import { refreshBotLicenses } from '../hooks/useBotLicense';
import { formatDateTime } from '../services/dateFormat';
import { refreshLicenses } from '../hooks/useLicense';
import { firstError } from '../api/envelope';

interface Props { userId: string; }

export default function UserGrantsAdmin(props: Props) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [catalog, setCatalog] = createSignal<GrantTypeDto[]>([]);
    const [summary, setSummary] = createSignal<UserGrantSummaryDto | null>(null);
    const [loading, setLoading] = createSignal(true);
    const [edits, setEdits] = createSignal<Record<string, number>>({});
    const [busy, setBusy] = createSignal(false);
    const [seats, setSeats] = createSignal<BotLicenseAssignmentDto[]>([]);
    const [graceDays, setGraceDays] = createSignal<Record<string, number>>({});
    // Display only: an untouched field sends null and the backend applies its own default.
    const defaultGraceDays = 3;

    async function loadCatalog() {
        const res = await api.grants.catalog();
        if (isOk(res.data)) setCatalog((res.data.value?.catalog ?? []) as GrantTypeDto[]);
    }

    async function loadSummary() {
        if (!props.userId) return;
        try {
            const [res, seatsRes] = await Promise.all([
                api.grants.userSummary(props.userId),
                api.botLicense.adminUserSeats(props.userId),
            ]);
            if (isOk(res.data)) setSummary(res.data.value as UserGrantSummaryDto);
            if (isOk(seatsRes.data)) setSeats(seatsRes.data.value.seats);
        } finally {
            setLoading(false);
        }
    }

    createEffect(() => {
        if (!props.userId) return;
        setLoading(true);
        void loadCatalog();
        void loadSummary();
    });

    useVisibilityPoll(() => { if (props.userId) void loadSummary(); }, 30_000);

    function directValue(type: string): number {
        const e = summary()?.entries.find(x => x.type === type);
        if (!e) return 0;
        const direct = e.sources.find(s => s.sourceTenantId === null);
        return direct?.value ?? 0;
    }

    function valueOf(type: string): number {
        const o = edits();
        if (o[type] !== undefined) return o[type];
        return directValue(type);
    }

    const lowersSeats = (c: GrantTypeDto) => c.kind === 'Seat' && valueOf(c.type) < directValue(c.type);
    const graceDaysOf = (type: string) => graceDays()[type] ?? defaultGraceDays;
    const seatsOf = (type: string) => seats().filter(seat => seat.licenseType === type);

    async function setDirect(type: string) {
        const value = valueOf(type);
        setBusy(true);
        try {
            const res = await api.grants.setUserDirect(props.userId, { type, value, note: null, graceDays: graceDays()[type] ?? null });
            if (isOk(res.data)) {
                toast?.info(t('GRANTS_DIRECT_SAVED'));
                refreshLicenses();
                refreshBotLicenses();
                const next = { ...edits() };
                delete next[type];
                setEdits(next);
                await loadSummary();
            } else {
                toast?.error(firstError(res.data) ?? t('GRANT_SAVE_FAILED'));
            }
        } finally {
            setBusy(false);
        }
    }

    async function revoke(grantId: string) {
        if (!await showConfirm(t('GRANTS_REVOKE_CONFIRM'), true)) return;
        const res = await api.grants.revoke(grantId);
        if (isOk(res.data)) {
            toast?.info(t('GRANTS_REVOKED'));
            refreshLicenses();
            await loadSummary();
        }
    }

    async function revokeSeat(assignment: BotLicenseAssignmentDto) {
        if (!await showConfirm(t('BOT_LICENSE_ADMIN_REVOKE_CONFIRM', assignment.botName ?? ''), true, { confirmLabel: t('BOT_LICENSE_ADMIN_REVOKE') })) return;
        const res = await api.botLicense.adminRevoke(assignment.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('BOT_LICENSE_UNASSIGN_FAILED')); return; }
        setSeats(res.data.value.seats);
        refreshBotLicenses();
        await loadSummary();
    }

    async function clearCooldown(assignment: BotLicenseAssignmentDto) {
        const res = await api.botLicense.adminClearCooldown(assignment.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('BOT_LICENSE_CLEAR_COOLDOWN_FAILED')); return; }
        setSeats(res.data.value.seats);
        await loadSummary();
    }

    return (
        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
            <h2 style={{ margin: 0 }}><Text val="USER_GRANTS_ADMIN_TITLE" /></h2>
            <p style={{ color: 'var(--dark-text-muted)', margin: 0, 'font-size': 'var(--font-size-sm)' }}>
                <Text val="USER_GRANTS_ADMIN_HELP" />
            </p>

            <Show when={!loading() && summary()} fallback={<div style={{ color: 'var(--dark-text-muted)' }}>...</div>}>
                <For each={catalog()}>{(c) => {
                    const entry = () => summary()!.entries.find(e => e.type === c.type);
                    return (
                        <div class="v" style={{ gap: 'var(--spacing-xs)', 'border-top': '1px solid var(--dark-border)', 'padding-top': 'var(--spacing-md)' }}>
                            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span style={{ 'font-weight': 600 }}>{c.displayName}</span>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>
                                        <Text val="USER_GRANTS_EFFECTIVE" />: {entry()?.effectiveValue ?? c.defaultValue} ({c.kind})
                                    </span>
                                </div>
                                <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                                    <input
                                        type="number"
                                        min="0"
                                        style={{ width: '5rem' }}
                                        value={valueOf(c.type)}
                                        onInput={e => setEdits({ ...edits(), [c.type]: parseInt(e.currentTarget.value) || 0 })}
                                    />
                                    <Show when={lowersSeats(c)}>
                                        <label class="h" style={{ gap: 'var(--spacing-xs)', 'align-items': 'center', 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                            <Text val="BOT_LICENSE_GRACE_DAYS" />
                                            <input
                                                type="number"
                                                min="0"
                                                style={{ width: '4rem' }}
                                                value={graceDaysOf(c.type)}
                                                onInput={e => setGraceDays({ ...graceDays(), [c.type]: Math.max(0, parseInt(e.currentTarget.value) || 0) })}
                                            />
                                        </label>
                                    </Show>
                                    <button class="btn btn-primary btn-sm" disabled={busy()} onClick={() => setDirect(c.type)}>
                                        <Text val="GRANTS_SAVE_DIRECT" />
                                    </button>
                                </div>
                            </div>
                            <Show when={c.kind === 'Seat'}>
                                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'margin-top': 'var(--spacing-sm)' }}>
                                    {t('BOT_LICENSE_ADMIN_SEATS_USED', seatsOf(c.type).length, entry()?.effectiveValue ?? c.defaultValue)}
                                </span>
                                <BotLicenseAssignmentList
                                    assignments={seatsOf(c.type)}
                                    actions={(assignment) => (
                                        <Show when={assignment.state === 'Cooldown'} fallback={
                                            <button class="btn btn-danger btn-sm" onClick={() => void revokeSeat(assignment)}>
                                                <Text val="BOT_LICENSE_ADMIN_REVOKE" />
                                            </button>
                                        }>
                                            <button class="btn btn-secondary btn-sm" onClick={() => void clearCooldown(assignment)}>
                                                <Text val="BOT_LICENSE_ADMIN_CLEAR_COOLDOWN" />
                                            </button>
                                        </Show>
                                    )}
                                />
                            </Show>
                            <Show when={(entry()?.sources.length ?? 0) > 0}>
                                <div style={{ 'margin-top': 'var(--spacing-sm)' }}>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}><Text val="MY_LICENSES_SOURCES" />:</span>
                                    <For each={entry()!.sources}>{(src) => (
                                        <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', padding: 'var(--spacing-xs) 0 var(--spacing-xs) var(--spacing-md)', 'font-size': 'var(--font-size-xs)' }}>
                                            <span>
                                                +{src.value}
                                                <Show when={src.sourceTenantId} fallback={
                                                    <span style={{ color: 'var(--accent-purple)', 'margin-left': 'var(--spacing-sm)' }}><Text val="MY_LICENSES_DIRECT" /></span>
                                                }>
                                                    <span style={{ color: 'var(--dark-text-muted)', 'margin-left': 'var(--spacing-sm)' }}>
                                                        ({src.sourceTenantName ?? src.sourceTenantId})
                                                    </span>
                                                </Show>
                                                <span style={{ color: 'var(--dark-text-muted)', 'margin-left': 'var(--spacing-sm)' }}>{formatDateTime(src.createdAt)}</span>
                                            </span>
                                            <button class="btn btn-danger btn-sm" onClick={() => revoke(src.grantId)} title={t('GRANTS_REVOKE')}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    )}</For>
                                </div>
                            </Show>
                        </div>
                    );
                }}</For>
            </Show>
        </div>
    );
}
